const SOCIAL_ICONS = {
  Website: {
    label: "Website",
    path: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39",
  },
  LinkedIn: {
    label: "LinkedIn",
    path: "M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zm-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93zM6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37z",
  },
  X: {
    label: "X",
    path: "M10.488 14.651L15.25 21h7l-7.858-10.478L20.93 3h-2.65l-5.117 5.886L8.75 3h-7l7.51 10.015L2.32 21h2.65zM16.25 19L5.75 5h2l10.5 14z",
  },
  Instagram: {
    label: "Instagram",
    path: "M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4zm9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8A1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5a5 5 0 0 1-5 5a5 5 0 0 1-5-5a5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3",
  },
} as const

export type CompanySocialKey = keyof typeof SOCIAL_ICONS

export type CompanySocialInput = {
  companyWebsite?: string | null
  companyLinkedin?: string | null
  companyTwitter?: string | null
  companyInstagram?: string | null
}

/** Only http(s) links are safe to render as an href — blocks javascript:/data: URIs. */
function isSafeHref(url: string | null | undefined): url is string {
  return typeof url === "string" && /^https?:\/\//i.test(url.trim())
}

function buildLinks(company: CompanySocialInput) {
  return (
    [
      { key: "Website" as const, url: company.companyWebsite },
      { key: "LinkedIn" as const, url: company.companyLinkedin },
      { key: "X" as const, url: company.companyTwitter },
      { key: "Instagram" as const, url: company.companyInstagram },
    ] as const
  ).filter((s): s is { key: CompanySocialKey; url: string } => isSafeHref(s.url))
}

export function CompanySocialLinks({
  company,
  className,
}: {
  company: CompanySocialInput
  className?: string
}) {
  const links = buildLinks(company)
  if (links.length === 0) return null

  return (
    <div
      className={["mkt-company-socials", className].filter(Boolean).join(" ")}
      aria-label="Company links"
    >
      {links.map((s) => {
        const icon = SOCIAL_ICONS[s.key]
        return (
          <a
            key={s.key}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={icon.label}
            title={icon.label}
            className="mkt-company-social"
          >
            <svg
              className="size-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path fill="currentColor" d={icon.path} />
            </svg>
          </a>
        )
      })}
    </div>
  )
}
